import { Row } from '@vivaldis/antd-ui'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { doesEmployeeNeedBIC, useCountryName } from '@vivaldis/common'
import { EmployeeProfileQuery_employee } from '../../../../typings/EmployeeProfileQuery'
import { Information } from '../../../../../Information'
import { MissingInformationTag } from '../../../MissingInformationTag'

interface Props {
  employee: EmployeeProfileQuery_employee
}

export const ContactRequiredInformation = ({ employee }: Props) => {
  const [t] = useTranslation('web_ui')

  const { profile, workProfile } = employee

  const bankAccount = workProfile?.bankAccount
  const bic = workProfile?.bic

  const buildingNumber = profile?.buildingNumber
  const street = profile?.street
  const streetNumber = profile?.streetNumber
  const city = profile?.city?.trim()
  const zip = profile?.zip
  const countryName = useCountryName(employee.profile?.country)

  const countryOfBirthName = useCountryName(
    employee.profile?.countryOfBirth || undefined
  )

  const placeOfBirth = employee.profile?.placeOfBirth

  const isMissingAddressInformation = useMemo(() => {
    return !street || !streetNumber || !zip || !city || !countryName
  }, [city, countryName, street, streetNumber, zip])

  const isMissingBic = useMemo(() => {
    if (bic) {
      return false
    }
    return doesEmployeeNeedBIC(bankAccount)
  }, [bankAccount, bic])

  return (
    <>
      <Information.InformationField label={t('employee_profile.bank_account')}>
        {bankAccount ?? <MissingInformationTag />}
      </Information.InformationField>
      {bic || isMissingBic ? (
        <Information.InformationField label={t('employee_profile.bic')}>
          {bic ?? <MissingInformationTag />}
        </Information.InformationField>
      ) : null}

      <Information.InformationField label={t('employee_profile.address')}>
        {isMissingAddressInformation ? (
          <MissingInformationTag />
        ) : (
          <>
            <Row justify="end">
              {street} {streetNumber} {buildingNumber}
            </Row>
            <Row justify="end">
              {zip} {city}
            </Row>
            <Row justify="end">{countryName}</Row>
          </>
        )}
      </Information.InformationField>

      <Information.InformationField
        label={t('employee_profile.country_of_birth')}
      >
        {countryOfBirthName ?? <MissingInformationTag />}
      </Information.InformationField>

      <Information.InformationField label={t('employee_profile.birthplace')}>
        {placeOfBirth ?? <MissingInformationTag />}
      </Information.InformationField>
    </>
  )
}
